import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPendingVendorChanges, approveDraftChanges, rejectDraftChanges, getPendingNewProducts, approveNewProduct, rejectNewProduct } from '@/data/adminVendorReview';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, X, AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function VendorUpdates() {
  const queryClient = useQueryClient();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectProductId, setRejectProductId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectType, setRejectType] = useState<'update' | 'new'>('update');
  
  // Pending vendor updates (draft changes to existing products)
  const { data: pendingProducts, isLoading } = useQuery({
    queryKey: ['pending-vendor-changes'],
    queryFn: getPendingVendorChanges,
  });
  
  // New products awaiting first-time approval
  const { data: pendingNewProducts, isLoading: isLoadingNew } = useQuery({
    queryKey: ['pending-new-products'],
    queryFn: getPendingNewProducts,
  });
  
  const approveMutation = useMutation({
    mutationFn: (productId: string) => approveDraftChanges(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-vendor-changes'] });
      toast.success('Changes approved and published');
    },
    onError: (error: any) => {
      toast.error('Failed to approve: ' + error.message);
    },
  });
  
  const rejectMutation = useMutation({
    mutationFn: ({ productId, reason }: { productId: string; reason?: string }) =>
      rejectDraftChanges(productId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-vendor-changes'] });
      toast.success('Changes rejected with feedback');
      setRejectDialogOpen(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      toast.error('Failed to reject: ' + error.message);
    },
  });
  
  // Mutations for new products
  const approveNewMutation = useMutation({
    mutationFn: (productId: string) => approveNewProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-new-products'] });
      toast.success('Product approved and published to marketplace');
    },
    onError: (error: any) => {
      toast.error('Failed to approve: ' + error.message);
    },
  });
  
  const rejectNewMutation = useMutation({
    mutationFn: ({ productId, reason }: { productId: string; reason?: string }) =>
      rejectNewProduct(productId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-new-products'] });
      toast.success('Product rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      toast.error('Failed to reject: ' + error.message);
    },
  });
  
  const handleReject = () => {
    if (!rejectProductId) return;
    if (rejectType === 'new') {
      rejectNewMutation.mutate({ productId: rejectProductId, reason: rejectionReason || undefined });
    } else {
      rejectMutation.mutate({ productId: rejectProductId, reason: rejectionReason || undefined });
    }
  };
  
  const newProductsCount = pendingNewProducts?.length || 0;
  const updatesCount = pendingProducts?.length || 0;
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Vendor Management</h1>
          <p className="text-muted-foreground">
            Review new vendor products and approve listing updates
          </p>
        </div>
        
        <Tabs defaultValue="new" className="space-y-6">
          <TabsList>
            <TabsTrigger value="new" className="gap-2">
              <Plus className="h-4 w-4" />
              New Products
              {newProductsCount > 0 && (
                <Badge variant="secondary" className="ml-1">{newProductsCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="updates" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Update Requests
              {updatesCount > 0 && (
                <Badge variant="secondary" className="ml-1">{updatesCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* New Products Tab */}
          <TabsContent value="new">
            {isLoadingNew ? (
              <div>Loading pending products...</div>
            ) : pendingNewProducts?.length === 0 ? (
              <Card className="p-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No new products awaiting approval</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {pendingNewProducts?.map((product) => (
                  <Card key={product.id} className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex gap-4">
                        {product.image && (
                          <img 
                            src={product.image} 
                            alt={product.title} 
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-xl mb-1">{product.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            Vendor: {product.vendor_name || 'Unknown'} ({product.vendor_email || 'No email'})
                          </p>
                          <p className="text-sm line-clamp-2">{product.description}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Pending Review
                      </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Price:</span>{' '}
                        <span className="font-medium">${product.price || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Categories:</span>{' '}
                        <span className="font-medium">
                          {Array.isArray(product.categories) ? product.categories.join(', ') : 'None'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Service Type:</span>{' '}
                        <span className="font-medium">{product.service_type || 'Not set'}</span>
                      </div>
                    </div>
                    
                    <Separator className="mb-4" />
                    
                    <div className="flex gap-3">
                      <Link to={`/product/${product.slug}`} target="_blank">
                        <Button variant="outline">Preview Product</Button>
                      </Link>
                      <Button 
                        onClick={() => approveNewMutation.mutate(product.id)}
                        disabled={approveNewMutation.isPending || rejectNewMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve & Publish
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => {
                          setRejectProductId(product.id);
                          setRejectType('new');
                          setRejectDialogOpen(true);
                        }}
                        disabled={approveNewMutation.isPending || rejectNewMutation.isPending}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Update Requests Tab */}
          <TabsContent value="updates">
        {isLoading ? (
          <div>Loading pending updates...</div>
        ) : pendingProducts?.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No pending vendor updates</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {pendingProducts?.map((product) => {
              const draft = product.draft_data as any;
              const hasChanges = draft && Object.keys(draft).length > 0;
              
              return (
                <Card key={product.id} className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="font-bold text-xl mb-1">{product.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Vendor: {product.vendor_name || 'Unknown'} ({product.vendor_email})
                      </p>
                    </div>
                    <Badge variant="outline">{product.status}</Badge>
                  </div>
                  
                  {hasChanges && (
                    <>
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        {/* LEFT: Current Live */}
                        <div>
                          <h4 className="font-semibold mb-3 text-sm text-muted-foreground">Current (Live)</h4>
                          <div className="space-y-3 text-sm">
                            <div>
                              <strong className="text-xs text-muted-foreground">Title:</strong>
                              <p className="mt-1">{product.title}</p>
                            </div>
                            {product.subtitle && (
                              <div>
                                <strong className="text-xs text-muted-foreground">Subtitle:</strong>
                                <p className="mt-1">{product.subtitle}</p>
                              </div>
                            )}
                            <div>
                              <strong className="text-xs text-muted-foreground">Description:</strong>
                              <p className="mt-1 line-clamp-4">{product.description || '(none)'}</p>
                            </div>
                            {product.booking_url && (
                              <div>
                                <strong className="text-xs text-muted-foreground">Booking URL:</strong>
                                <p className="mt-1 truncate text-primary">{product.booking_url}</p>
                              </div>
                            )}
                            {product.image && (
                              <div>
                                <strong className="text-xs text-muted-foreground">Main Image:</strong>
                                <img src={product.image} alt="Current" className="mt-1 w-full h-32 object-cover rounded" />
                              </div>
                            )}
                            {product.profile_image && (
                              <div>
                                <strong className="text-xs text-muted-foreground">Profile Image:</strong>
                                <img src={product.profile_image} alt="Current Profile" className="mt-1 w-16 h-16 rounded-full" />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* RIGHT: Proposed Changes */}
                        <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
                          <h4 className="font-semibold mb-3 text-sm text-amber-900">Proposed Changes</h4>
                          <div className="space-y-3 text-sm">
                            {draft.title && (
                              <div>
                                <strong className="text-xs text-amber-700">Title:</strong>
                                <p className="mt-1 text-amber-900">{draft.title}</p>
                              </div>
                            )}
                            {draft.subtitle && (
                              <div>
                                <strong className="text-xs text-amber-700">Subtitle:</strong>
                                <p className="mt-1 text-amber-900">{draft.subtitle}</p>
                              </div>
                            )}
                            {draft.description && (
                              <div>
                                <strong className="text-xs text-amber-700">Description:</strong>
                                <p className="mt-1 line-clamp-4 text-amber-900">{draft.description}</p>
                              </div>
                            )}
                            {draft.booking_url && (
                              <div>
                                <strong className="text-xs text-amber-700">Booking URL:</strong>
                                <p className="mt-1 truncate text-amber-700">{draft.booking_url}</p>
                              </div>
                            )}
                            {draft.best_for && (
                              <div>
                                <strong className="text-xs text-amber-700">Best For:</strong>
                                <p className="mt-1 text-amber-900">{draft.best_for.join(', ')}</p>
                              </div>
                            )}
                            {draft.categories && (
                              <div>
                                <strong className="text-xs text-amber-700">Categories:</strong>
                                <p className="mt-1 text-amber-900">{draft.categories.join(', ')}</p>
                              </div>
                            )}
                            {draft.feature_highlights && (
                              <div>
                                <strong className="text-xs text-amber-700">Feature Highlights:</strong>
                                <p className="mt-1 text-amber-900">{draft.feature_highlights.length} items</p>
                              </div>
                            )}
                            {draft.image && (
                              <div>
                                <strong className="text-xs text-amber-700">Main Image:</strong>
                                <img src={draft.image} alt="Proposed" className="mt-1 w-full h-32 object-cover rounded border-2 border-amber-200" />
                              </div>
                            )}
                            {draft.profile_image && (
                              <div>
                                <strong className="text-xs text-amber-700">Profile Image:</strong>
                                <img src={draft.profile_image} alt="Proposed Profile" className="mt-1 w-16 h-16 rounded-full border-2 border-amber-200" />
                              </div>
                            )}
                            {!draft.title && !draft.subtitle && !draft.description && !draft.booking_url && !draft.image && !draft.profile_image && (
                              <p className="text-amber-600 italic">No specific field changes in draft_data</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Separator className="mb-6" />
                    </>
                  )}
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => approveMutation.mutate(product.id)}
                      disabled={approveMutation.isPending || rejectMutation.isPending}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Approve & Publish
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => {
                        setRejectProductId(product.id);
                        setRejectType('update');
                        setRejectDialogOpen(true);
                      }}
                      disabled={approveMutation.isPending || rejectMutation.isPending}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Reject Changes
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Changes</DialogTitle>
            <DialogDescription>
              Provide feedback to the vendor about why these changes were rejected
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rejection_reason">Rejection Reason</Label>
              <Textarea
                id="rejection_reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Explain what needs to be improved..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending || rejectNewMutation.isPending}
            >
              {(rejectMutation.isPending || rejectNewMutation.isPending) ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
